import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { leadSourcesApi, LeadSourceDto, LeadSourceType } from '../../services/settingsService';

/**
 * P8b — שיוך סוכן/ארגון מפנה (מהספרייה P8a) + עד 4 ערכי עמלה פר-תיק.
 *
 * - שני הבוררים (סוכן, ארגון) עצמאיים ואופציונליים, גלויים לכל הצוות.
 * - ערכי העמלה מוצגים רק כאשר showCommissions=true (ביצירה — לכל הצוות; בעריכה — רק ל-CanViewClientFinance).
 * - בבחירת סוכן/ארגון מתבצע auto-fill של ברירות-המחדל מהספרייה; כל שדה עמלה אופציונלי וניתן לניקוי → NULL.
 *   ה-auto-fill הוא נוחות בלבד; הערכים חיים על התיק ואינם משפיעים על הספרייה.
 */
export interface AgentOrgValue {
  agentSourceId?: number | null;
  organizationSourceId?: number | null;
  agentSigningCommissionAmount?: number | null;
  agentApprovalCommissionPercentage?: number | null;
  organizationSigningCommissionAmount?: number | null;
  organizationApprovalCommissionPercentage?: number | null;
}

interface Props {
  value: AgentOrgValue;
  onChange: (patch: Partial<AgentOrgValue>) => void;
  /** האם להציג את שדות העמלה (יצירה → true לכל הצוות; עריכה → canViewClientFinance) */
  showCommissions: boolean;
  /** נעילת כל הסקשן (למשל בתצוגה בלבד) */
  disabled?: boolean;
}

// מטמון ברמת המודול — הרשימות נטענות פעם אחת ומשותפות לכל המופעים בעמוד.
let agentsCache: LeadSourceDto[] | null = null;
let orgsCache: LeadSourceDto[] | null = null;
let agentsPromise: Promise<LeadSourceDto[]> | null = null;
let orgsPromise: Promise<LeadSourceDto[]> | null = null;

const loadOnce = (
  type: LeadSourceType,
  cache: LeadSourceDto[] | null,
  setCache: (v: LeadSourceDto[]) => void,
  getPromise: () => Promise<LeadSourceDto[]> | null,
  setPromise: (p: Promise<LeadSourceDto[]> | null) => void
): Promise<LeadSourceDto[]> => {
  if (cache) return Promise.resolve(cache);
  let p = getPromise();
  if (!p) {
    p = leadSourcesApi
      .getAll(type)
      .then((list) => {
        setCache(list);
        return list;
      })
      .catch((err) => {
        setPromise(null); // מאפשר ניסיון חוזר לאחר כשל
        throw err;
      });
    setPromise(p);
  }
  return p;
};

const Section = styled.div`
  grid-column: 1 / -1;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
`;

const Party = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  background: white;
  color: var(--text-primary);
  direction: rtl;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: var(--background-secondary);
  }
`;

const CommissionRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SmallLabel = styled.label`
  font-size: 12px;
  color: var(--text-secondary);
`;

const NumberInput = styled.input`
  width: 130px;
  padding: 8px 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);
  direction: rtl;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }
  &:disabled {
    opacity: 0.6;
    background: var(--background-secondary);
  }
`;

const Hint = styled.span`
  font-size: 11px;
  color: var(--text-secondary);
`;

const parseNum = (v: string): number | null => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const AgentOrgAssignmentSection: React.FC<Props> = ({ value, onChange, showCommissions, disabled }) => {
  const [agents, setAgents] = useState<LeadSourceDto[]>(agentsCache ?? []);
  const [organizations, setOrganizations] = useState<LeadSourceDto[]>(orgsCache ?? []);

  useEffect(() => {
    let mounted = true;
    loadOnce(LeadSourceType.Agent, agentsCache, (v) => (agentsCache = v), () => agentsPromise, (p) => (agentsPromise = p))
      .then((list) => { if (mounted) setAgents(list); })
      .catch(() => { /* טעינה נכשלה — הבורר יציג את הערך הנוכחי בלבד */ });
    loadOnce(LeadSourceType.Organization, orgsCache, (v) => (orgsCache = v), () => orgsPromise, (p) => (orgsPromise = p))
      .then((list) => { if (mounted) setOrganizations(list); })
      .catch(() => { /* טעינה נכשלה */ });
    return () => { mounted = false; };
  }, []);

  // בורר עם אפשרויות: פעילים בלבד + הנבחר הנוכחי (גם אם הושבת), כדי שלא "ייעלם".
  const optionsFor = (list: LeadSourceDto[], selectedId?: number | null) =>
    list.filter((e) => e.isActive || e.id === selectedId);

  const handleAgentChange = (idStr: string) => {
    const id = idStr ? Number(idStr) : null;
    if (!showCommissions) {
      // ללא גישה פיננסית: משנים רק את השיוך; השרת ינקה/ישמור את ערכי העמלה לפי ההיגיון שלו.
      onChange({ agentSourceId: id });
      return;
    }
    if (id === null) {
      onChange({ agentSourceId: null, agentSigningCommissionAmount: null, agentApprovalCommissionPercentage: null });
    } else {
      const entry = agents.find((a) => a.id === id);
      onChange({
        agentSourceId: id,
        agentSigningCommissionAmount: entry?.defaultSigningCommissionAmount ?? null,
        agentApprovalCommissionPercentage: entry?.defaultApprovalCommissionPercentage ?? null,
      });
    }
  };

  const handleOrgChange = (idStr: string) => {
    const id = idStr ? Number(idStr) : null;
    if (!showCommissions) {
      onChange({ organizationSourceId: id });
      return;
    }
    if (id === null) {
      onChange({ organizationSourceId: null, organizationSigningCommissionAmount: null, organizationApprovalCommissionPercentage: null });
    } else {
      const entry = organizations.find((o) => o.id === id);
      onChange({
        organizationSourceId: id,
        organizationSigningCommissionAmount: entry?.defaultSigningCommissionAmount ?? null,
        organizationApprovalCommissionPercentage: entry?.defaultApprovalCommissionPercentage ?? null,
      });
    }
  };

  return (
    <Section>
      <SectionTitle>סוכן / ארגון מפנה</SectionTitle>

      {/* סוכן */}
      <Party>
        <Label>סוכן מפנה</Label>
        <Select
          value={value.agentSourceId ?? ''}
          onChange={(e) => handleAgentChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">ללא סוכן</option>
          {optionsFor(agents, value.agentSourceId).map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </Select>

        {showCommissions && value.agentSourceId != null && (
          <>
            <CommissionRow>
              <Field>
                <SmallLabel>עמלת חתימה (₪)</SmallLabel>
                <NumberInput
                  type="number"
                  min="0"
                  step="0.01"
                  value={value.agentSigningCommissionAmount ?? ''}
                  onChange={(e) => onChange({ agentSigningCommissionAmount: parseNum(e.target.value) })}
                  disabled={disabled}
                  placeholder="ללא"
                />
              </Field>
              <Field>
                <SmallLabel>עמלת אישור (%)</SmallLabel>
                <NumberInput
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={value.agentApprovalCommissionPercentage ?? ''}
                  onChange={(e) => onChange({ agentApprovalCommissionPercentage: parseNum(e.target.value) })}
                  disabled={disabled}
                  placeholder="ללא"
                />
              </Field>
            </CommissionRow>
            <Hint>הועתק מברירת-מחדל של הסוכן · ניתן לעריכה או להשארה ריק · משפיע על תיק זה בלבד</Hint>
          </>
        )}
      </Party>

      {/* ארגון */}
      <Party>
        <Label>ארגון מפנה</Label>
        <Select
          value={value.organizationSourceId ?? ''}
          onChange={(e) => handleOrgChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">ללא ארגון</option>
          {optionsFor(organizations, value.organizationSourceId).map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </Select>

        {showCommissions && value.organizationSourceId != null && (
          <>
            <CommissionRow>
              <Field>
                <SmallLabel>עמלת חתימה (₪)</SmallLabel>
                <NumberInput
                  type="number"
                  min="0"
                  step="0.01"
                  value={value.organizationSigningCommissionAmount ?? ''}
                  onChange={(e) => onChange({ organizationSigningCommissionAmount: parseNum(e.target.value) })}
                  disabled={disabled}
                  placeholder="ללא"
                />
              </Field>
              <Field>
                <SmallLabel>עמלת אישור (%)</SmallLabel>
                <NumberInput
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={value.organizationApprovalCommissionPercentage ?? ''}
                  onChange={(e) => onChange({ organizationApprovalCommissionPercentage: parseNum(e.target.value) })}
                  disabled={disabled}
                  placeholder="ללא"
                />
              </Field>
            </CommissionRow>
            <Hint>הועתק מברירת-מחדל של הארגון · ניתן לעריכה או להשארה ריק · משפיע על תיק זה בלבד</Hint>
          </>
        )}
      </Party>
    </Section>
  );
};

export default AgentOrgAssignmentSection;
